import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { ChevronDown, Menu, X, Phone, Mail, MapPin, MessageCircle, Youtube } from "lucide-react";
import { SITE, SISTER_CONCERNS } from "../data/site";
import { PRODUCTS } from "../data/products";

const navLinkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors hover:text-[#FF9900] ${
        isActive ? "text-[#FF9900]" : "text-slate-300"
    }`;

const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
    <MessageCircle className={className} aria-hidden="true" />
);

const Header = () => {
    const [open, setOpen] = useState(false);
    const [drop, setDrop] = useState(false);

    return (
        <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-800 bg-[#0A0B10]/95">
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
                <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3">
                    <img
                        src={SITE.logo}
                        alt="Morningstar Enterprises logo"
                        className="h-10 w-auto"
                    />
                </Link>

                <nav aria-label="Main navigation" className="hidden items-center gap-7 lg:flex">
                    <NavLink to="/" end className={navLinkClass} data-testid="nav-home">
                        Home
                    </NavLink>
                    <div
                        className="relative"
                        onMouseEnter={() => setDrop(true)}
                        onMouseLeave={() => setDrop(false)}
                    >
                        <button
                            data-testid="nav-products-toggle"
                            aria-expanded={drop}
                            className="flex items-center gap-1 text-sm font-medium text-slate-300 transition-colors hover:text-[#FF9900]"
                            onClick={() => setDrop((v) => !v)}
                        >
                            Products <ChevronDown className="h-4 w-4" />
                        </button>
                        {drop && (
                            <div
                                data-testid="nav-products-dropdown"
                                className="absolute left-1/2 top-full w-[30rem] -translate-x-1/2 rounded-xl border border-slate-700 bg-[#12141E] p-4 shadow-2xl shadow-black/60"
                            >
                                <div className="grid grid-cols-2 gap-1">
                                    {PRODUCTS.map((p) => (
                                        <Link
                                            key={p.slug}
                                            to={`/products/${p.slug}`}
                                            data-testid={`nav-product-${p.slug}`}
                                            className="rounded-lg px-3 py-2 transition-colors hover:bg-[#1A1D2B]"
                                            onClick={() => setDrop(false)}
                                        >
                                            <span className="block text-sm font-semibold text-slate-100">
                                                {p.short}
                                            </span>
                                            <span className="block text-xs text-slate-400">
                                                {p.category}
                                            </span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <NavLink to="/about" className={navLinkClass} data-testid="nav-about">
                        About Us
                    </NavLink>
                    <NavLink to="/contact" className={navLinkClass} data-testid="nav-contact">
                        Contact
                    </NavLink>
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href={SITE.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="header-whatsapp-cta"
                        className="hidden items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-black shadow-[0_0_18px_rgba(37,211,102,0.45)] transition-transform hover:scale-105 sm:flex"
                    >
                        <WhatsAppIcon className="h-4 w-4" /> WhatsApp Enquiry
                    </a>
                    <button
                        data-testid="mobile-menu-toggle"
                        aria-label="Open menu"
                        className="rounded-lg border border-slate-700 p-2 text-slate-200 lg:hidden"
                        onClick={() => setOpen(true)}
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                </div>
            </div>

            {open && (
                <div
                    data-testid="mobile-drawer"
                    className="fixed inset-0 z-50 overflow-y-auto bg-[#0A0B10] p-6 lg:hidden"
                >
                    <div className="flex items-center justify-between">
                        <img
                            src={SITE.logo}
                            alt="Morningstar Enterprises logo"
                            className="h-10 w-auto"
                        />
                        <button
                            data-testid="mobile-menu-close"
                            aria-label="Close menu"
                            className="rounded-lg border border-slate-700 p-2 text-slate-200"
                            onClick={() => setOpen(false)}
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    <nav aria-label="Mobile navigation" className="mt-10 flex flex-col gap-1">
                        <Link to="/" data-testid="mobile-nav-home" onClick={() => setOpen(false)} className="rounded-lg px-3 py-3 font-display text-2xl uppercase tracking-wide text-slate-100 hover:bg-[#1A1D2B]">
                            Home
                        </Link>
                        <p className="mt-4 px-3 text-xs font-semibold uppercase tracking-widest text-slate-500">
                            Products
                        </p>
                        {PRODUCTS.map((p) => (
                            <Link
                                key={p.slug}
                                to={`/products/${p.slug}`}
                                data-testid={`mobile-nav-product-${p.slug}`}
                                onClick={() => setOpen(false)}
                                className="rounded-lg px-3 py-2.5 text-base text-slate-200 hover:bg-[#1A1D2B]"
                            >
                                {p.name}
                            </Link>
                        ))}
                        <Link to="/about" data-testid="mobile-nav-about" onClick={() => setOpen(false)} className="mt-4 rounded-lg px-3 py-3 font-display text-2xl uppercase tracking-wide text-slate-100 hover:bg-[#1A1D2B]">
                            About Us
                        </Link>
                        <Link to="/contact" data-testid="mobile-nav-contact" onClick={() => setOpen(false)} className="rounded-lg px-3 py-3 font-display text-2xl uppercase tracking-wide text-slate-100 hover:bg-[#1A1D2B]">
                            Contact
                        </Link>
                        <a
                            href={SITE.whatsapp}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="mobile-whatsapp-cta"
                            className="mt-6 flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-4 py-3 font-semibold text-black"
                        >
                            <WhatsAppIcon /> Chat on WhatsApp
                        </a>
                    </nav>
                </div>
            )}
        </header>
    );
};

const Footer = () => (
    <footer className="border-t border-slate-800 bg-[#07080C]">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 pb-10 pt-16 sm:px-6 md:grid-cols-2 lg:grid-cols-4">
            <div>
                <img
                    src={SITE.logo}
                    alt="Morningstar Enterprises logo"
                    className="h-11 w-auto"
                />
                <p className="mt-4 text-sm leading-relaxed text-slate-400">
                    Kochi's LED display and AV technology company since {SITE.established}.
                    Scrolling LED boards, video walls, interactive panels and smart
                    classroom solutions across {SITE.regions}.
                </p>
                <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-slate-500">
                    NSIC · MSME ZED Certified
                </p>
                <p className="mt-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
                    Member — SME Chamber of India
                </p>
                <p className="mt-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
                    Member — The Cochin Chamber of Commerce &amp; Industry
                </p>
            </div>
            <nav aria-label="Product links">
                <h2 className="font-display text-lg font-semibold uppercase tracking-wide text-slate-200">
                    Products
                </h2>
                <ul className="mt-4 space-y-2">
                    {PRODUCTS.map((p) => (
                        <li key={p.slug}>
                            <Link
                                to={`/products/${p.slug}`}
                                data-testid={`footer-product-${p.slug}`}
                                className="text-sm text-slate-400 transition-colors hover:text-[#FF9900]"
                            >
                                {p.name}
                            </Link>
                        </li>
                    ))}
                </ul>
            </nav>
            <nav aria-label="Company links">
                <h2 className="font-display text-lg font-semibold uppercase tracking-wide text-slate-200">
                    Company
                </h2>
                <ul className="mt-4 space-y-2 text-sm text-slate-400">
                    <li><Link to="/" data-testid="footer-home" className="transition-colors hover:text-[#FF9900]">Home</Link></li>
                    <li><Link to="/about" data-testid="footer-about" className="transition-colors hover:text-[#FF9900]">About Us</Link></li>
                    <li><Link to="/contact" data-testid="footer-contact" className="transition-colors hover:text-[#FF9900]">Contact &amp; Support</Link></li>
                    <li>
                        <a href={SITE.youtube} target="_blank" rel="noopener noreferrer" data-testid="footer-youtube" className="inline-flex items-center gap-2 transition-colors hover:text-[#FF9900]">
                            <Youtube className="h-4 w-4" /> YouTube Channel
                        </a>
                    </li>
                </ul>
                <p className="mt-6 text-xs font-semibold uppercase tracking-widest text-slate-500">
                    Sister Concerns
                </p>
                <ul className="mt-2 space-y-2 text-sm text-slate-400">
                    {SISTER_CONCERNS.map((s) => (
                        <li key={s.name}>
                            <a
                                href={s.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`footer-sister-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                className="transition-colors hover:text-[#FF9900]"
                            >
                                {s.name}, {s.location}
                            </a>
                        </li>
                    ))}
                </ul>
            </nav>
            <div>
                <h2 className="font-display text-lg font-semibold uppercase tracking-wide text-slate-200">
                    Contact
                </h2>
                <address className="mt-4 space-y-3 text-sm not-italic text-slate-400">
                    <p>
                        <a
                            href="https://www.google.com/maps/search/?api=1&query=Morning+Star+Enterprises,+Vylassery+Road,+Palarivattom,+Kochi+682025"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-address-link"
                            className="flex gap-2 transition-colors hover:text-[#FF9900]"
                        >
                            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#FF9900]" />
                            {SITE.address}
                        </a>
                    </p>
                    <p>
                        <a href={SITE.phoneTel} data-testid="footer-phone" className="flex gap-2 transition-colors hover:text-[#FF9900]">
                            <Phone className="mt-0.5 h-4 w-4 shrink-0 text-[#FF9900]" />
                            {SITE.phoneDisplay}
                        </a>
                    </p>
                    {SITE.emails.map((e) => (
                        <p key={e}>
                            <a href={`mailto:${e}`} data-testid={`footer-email-${e.split("@")[0]}`} className="flex gap-2 transition-colors hover:text-[#FF9900]">
                                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#FF9900]" />
                                {e}
                            </a>
                        </p>
                    ))}
                </address>
                <a
                    href={SITE.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-whatsapp-cta"
                    className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#25D366]/50 px-4 py-2 text-sm font-semibold text-[#25D366] transition-colors hover:bg-[#25D366] hover:text-black"
                >
                    <WhatsAppIcon className="h-4 w-4" /> Chat on WhatsApp
                </a>
            </div>
        </div>
        <div className="border-t border-slate-800/70 py-5 text-center text-xs text-slate-500">
            <p className="mx-auto max-w-5xl px-4 leading-relaxed">
                Serving Kochi, Ernakulam, Thrissur, Kozhikode, Thiruvananthapuram, Kollam,
                Kottayam, Alappuzha, Pathanamthitta, Idukki, Palakkad, Malappuram, Wayanad,
                Kannur, Kasaragod and every district of Kerala — plus Tamil Nadu &amp; Karnataka.
            </p>
            <p className="mt-3">
                © {new Date().getFullYear()} Morning Star Enterprises, Kochi. LED display &amp; AV specialists since 2009.
            </p>
        </div>
    </footer>
);

const FloatingWhatsApp = () => (
    <a
        href={SITE.whatsapp}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-whatsapp-button"
        aria-label="Chat with Morningstar Enterprises on WhatsApp"
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-black shadow-[0_0_24px_rgba(37,211,102,0.55)] transition-transform hover:scale-110"
    >
        <WhatsAppIcon className="h-7 w-7" />
    </a>
);

export const Layout = () => (
    <div className="min-h-screen bg-[#0A0B10] text-slate-100">
        <Header />
        <main className="pt-16">
            <Outlet />
        </main>
        <Footer />
        <FloatingWhatsApp />
    </div>
);
